import { supabase } from './supabase';

export interface FollowRequest {
  id: string;
  follower_id: string;
  following_id: string;
  status: 'pending' | 'accepted' | 'rejected';
  created_at: string;
  profiles?: {
    username: string;
    avatar_url: string | null;
    artist_types: string[];
  };
}

export async function sendFollowRequest(userId: string) {
  const { data: existingRequest, error: checkError } = await supabase
    .from('follow_requests')
    .select()
    .eq('follower_id', (await supabase.auth.getUser()).data.user?.id)
    .eq('following_id', userId)
    .single();

  if (existingRequest) {
    throw new Error('Follow request already sent');
  }

  const { data, error } = await supabase
    .from('follow_requests')
    .insert([{
      follower_id: (await supabase.auth.getUser()).data.user?.id,
      following_id: userId,
      status: 'pending'
    }])
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function acceptFollowRequest(requestId: string) {
  const user = await supabase.auth.getUser();
  if (!user.data.user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('follow_requests')
    .update({ status: 'accepted' })
    .match({
      id: requestId,
      following_id: user.data.user.id,
      status: 'pending'
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function rejectFollowRequest(requestId: string) {
  const user = await supabase.auth.getUser();
  if (!user.data.user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('follow_requests')
    .update({ status: 'rejected' })
    .match({
      id: requestId,
      following_id: user.data.user.id,
      status: 'pending'
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getFollowRequests() {
  const { data, error } = await supabase
    .from('follow_requests')
    .select(`
      *,
      profiles!follow_requests_follower_id_fkey (
        username,
        avatar_url,
        artist_types
      )
    `)
    .eq('following_id', (await supabase.auth.getUser()).data.user?.id)
    .eq('status', 'pending')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

export async function getFollowRequestStatus(userId: string) {
  if (!userId) return 'none';

  const { data: sentRequest } = await supabase
    .from('follow_requests')
    .select('status')
    .eq('follower_id', (await supabase.auth.getUser()).data.user?.id)
    .eq('following_id', userId)
    .maybeSingle();

  // Check if there's an accepted request in the other direction
  const { data: receivedRequest } = await supabase
    .from('follow_requests')
    .select('status')
    .eq('follower_id', userId)
    .eq('following_id', (await supabase.auth.getUser()).data.user?.id)
    .eq('status', 'accepted')
    .maybeSingle();

  // If either direction is accepted, they're friends
  if (sentRequest?.status === 'accepted' || receivedRequest?.status === 'accepted') {
    return 'accepted';
  }

  return sentRequest?.status || 'none';
}

export async function getFollowerStats(userId: string) {
  const { data: followers, error: followersError } = await supabase
    .from('follow_requests')
    .select('id')
    .eq('following_id', userId)
    .eq('status', 'accepted');

  const { data: following, error: followingError } = await supabase
    .from('follow_requests')
    .select('id')
    .eq('follower_id', userId)
    .eq('status', 'accepted');

  if (followersError || followingError) {
    throw followersError || followingError;
  }

  return {
    followers: followers?.length || 0,
    following: following?.length || 0
  };
}